import { z } from "zod";

const optional = () => z.string().optional().or(z.literal(""));

const GSTIN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const PAN = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

export const clientFormSchema = z
  .object({
    // identity
    name: z.string().min(2, "Company name is required"),
    code: z
      .string()
      .min(2, "Client code is required")
      .max(32)
      .regex(/^[A-Za-z0-9-]+$/, "Letters, numbers and hyphen only"),
    client_type: z.enum([
      "oem",
      "trader",
      "distributor",
      "fabricator",
      "contractor",
      "government",
      "end_user",
    ]),
    industry_segment: optional(),
    lead_source: optional(),
    onboarded_on: optional(),
    is_active: z.boolean(),

    // reach
    work_phone: z.string().max(20).optional().or(z.literal("")),
    email: z.string().email("Enter a valid email").optional().or(z.literal("")),
    website: optional(),
    groups: optional(),
    show_primary_contact_on_docs: z.boolean(),

    // registered address
    addr1: z.string().min(3, "Address line 1 is required"),
    addr2: optional(),
    city: z.string().min(2, "City is required"),
    state: z.string().min(2, "State is required"),
    pincode: z.string().regex(/^[1-9][0-9]{5}$/, "6-digit PIN code"),

    // statutory
    gst_treatment: z.enum([
      "registered",
      "unregistered",
      "composition",
      "sez",
      "export",
      "deemed_export",
    ]),
    gst_no: optional(),
    pan_no: optional(),
    cin_no: optional(),
    msme_udyam_no: optional(),
    place_of_supply: optional(),
    tds_applicable: z.boolean(),
    tcs_applicable: z.boolean(),

    // commercial
    credit_limit: z.coerce.number().min(0, "Cannot be negative"),
    credit_days: z.coerce.number().int().min(0).max(365),
    payment_terms: z.enum([
      "advance",
      "against_delivery",
      "net_15",
      "net_30",
      "net_45",
      "net_60",
      "net_90",
      "letter_of_credit",
    ]),
    price_list: z.enum(["standard", "oem", "distributor", "project", "export"]),
    rate_basis: z.enum(["per_kg", "per_ton", "per_piece", "per_meter"]),
    currency: z.string().min(3).max(3),
    opening_balance: z.coerce.number(),
    opening_balance_as_of: optional(),

    // dispatch
    delivery_terms: z.enum(["ex_works", "fob", "cif", "door_delivery"]),
    freight_terms: z.enum(["paid", "to_pay", "included"]),
    preferred_transporter: optional(),
    packing_type: z.enum(["bundled", "loose", "strapped", "wooden_pallet", "as_per_po"]),
    dispatch_instructions: optional(),

    // quality
    requires_test_certificate: z.boolean(),
    requires_third_party_inspection: z.boolean(),
    quality_standard: optional(),
    tolerance_notes: optional(),

    notes: optional(),
  })
  .superRefine((values, ctx) => {
    const needsGstin = values.gst_treatment === "registered" || values.gst_treatment === "composition";
    if (needsGstin && !values.gst_no?.trim()) {
      ctx.addIssue({ code: "custom", path: ["gst_no"], message: "GSTIN required for this treatment" });
    }
    if (values.gst_no?.trim() && !GSTIN.test(values.gst_no.trim().toUpperCase())) {
      ctx.addIssue({ code: "custom", path: ["gst_no"], message: "Invalid GSTIN format" });
    }
    if (values.pan_no?.trim() && !PAN.test(values.pan_no.trim().toUpperCase())) {
      ctx.addIssue({ code: "custom", path: ["pan_no"], message: "Invalid PAN format" });
    }
    if (values.payment_terms !== "advance" && values.credit_days === 0) {
      ctx.addIssue({
        code: "custom",
        path: ["credit_days"],
        message: "Set credit days for non-advance terms",
      });
    }
    if (values.opening_balance !== 0 && !values.opening_balance_as_of?.trim()) {
      ctx.addIssue({
        code: "custom",
        path: ["opening_balance_as_of"],
        message: "Set the as-of date for opening balance",
      });
    }
  });

export type ClientFormValues = z.infer<typeof clientFormSchema>;

export const emptyClientForm: ClientFormValues = {
  name: "",
  code: "",
  client_type: "end_user",
  industry_segment: "",
  lead_source: "",
  onboarded_on: "",
  is_active: true,

  work_phone: "",
  email: "",
  website: "",
  groups: "",
  show_primary_contact_on_docs: false,

  addr1: "",
  addr2: "",
  city: "",
  state: "",
  pincode: "",

  gst_treatment: "registered",
  gst_no: "",
  pan_no: "",
  cin_no: "",
  msme_udyam_no: "",
  place_of_supply: "",
  tds_applicable: false,
  tcs_applicable: false,

  credit_limit: 0,
  credit_days: 0,
  payment_terms: "advance",
  price_list: "standard",
  rate_basis: "per_kg",
  currency: "INR",
  opening_balance: 0,
  opening_balance_as_of: "",

  delivery_terms: "ex_works",
  freight_terms: "to_pay",
  preferred_transporter: "",
  packing_type: "bundled",
  dispatch_instructions: "",

  requires_test_certificate: false,
  requires_third_party_inspection: false,
  quality_standard: "",
  tolerance_notes: "",

  notes: "",
};

export const contactFormSchema = z.object({
  name: z.string().min(2, "Name is required"),
  designation: optional(),
  department: optional(),
  mobile: optional(),
  alt_mobile: optional(),
  email: z.string().email("Enter a valid email").optional().or(z.literal("")),
  tags: z.array(z.string()),
  is_primary: z.boolean(),
  is_active: z.boolean(),
});

export type ContactFormValues = z.infer<typeof contactFormSchema>;

export const emptyContactForm: ContactFormValues = {
  name: "",
  designation: "",
  department: "",
  mobile: "",
  alt_mobile: "",
  email: "",
  tags: [],
  is_primary: false,
  is_active: true,
};

export const addressFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  addr1: z.string().min(3, "Address is required"),
  addr2: optional(),
  city: z.string().min(2, "City is required"),
  state: z.string().min(2, "State is required"),
  pincode: z.string().regex(/^[1-9][0-9]{5}$/, "6-digit PIN code"),
  address_type: z.enum(["billing", "shipping", "other"]),
  is_default: z.boolean(),
  notes: optional(),
});

export type AddressFormValues = z.infer<typeof addressFormSchema>;

export const emptyAddressForm: AddressFormValues = {
  title: "Address",
  addr1: "",
  addr2: "",
  city: "",
  state: "",
  pincode: "",
  address_type: "billing",
  is_default: false,
  notes: "",
};

export const noteFormSchema = z.object({
  body: z.string().min(1, "Note is required"),
});

export type NoteFormValues = z.infer<typeof noteFormSchema>;

export const reminderFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  due_at: optional(),
  is_done: z.boolean(),
});

export type ReminderFormValues = z.infer<typeof reminderFormSchema>;
